import { toast, ToastOptions } from "react-toastify";

const PERSISTED_TOAST_KEY = "persistedToastMessage";

// Subset of ToastOptions safe to JSON.stringify (no functions like onClose, etc.) for showPersistedToast
interface SerializableToastOptions {
  style?: React.CSSProperties;
  className?: string;
  bodyClassName?: string;
}

interface PersistedToastConfig {
  message: string;
  type: "success" | "error" | "info" | "warning";
  extraOptions?: SerializableToastOptions;
}

export const notify = {
  success: (message: string, extraOptions?: ToastOptions) =>
    toast.success(message, {
      autoClose: 6000,
      ...extraOptions,
    }),
  error: (message: string, extraOptions?: ToastOptions) =>
    toast.error(message, {
      autoClose: false,
      ...extraOptions,
    }),
  info: (message: string, extraOptions?: ToastOptions) =>
    toast.info(message, {
      autoClose: false,
      ...extraOptions,
    }),
  warn: (message: string, extraOptions?: ToastOptions) =>
    toast.warn(message, {
      autoClose: false,
      ...extraOptions,
    }),
  isToastActive: (toastId: string) => {
    return toast.isActive(toastId);
  },
  // persist toast message (on page load/navigation)
  persistToast: (toastConfig: PersistedToastConfig) => {
    sessionStorage.setItem(PERSISTED_TOAST_KEY, JSON.stringify(toastConfig));
  },
  showPersistedToast: async () => {
    const persisted = sessionStorage.getItem(PERSISTED_TOAST_KEY);
    if (persisted) {
      let parsed: PersistedToastConfig;
      try {
        parsed = JSON.parse(persisted) as PersistedToastConfig;
      } catch {
        sessionStorage.removeItem(PERSISTED_TOAST_KEY);
        return;
      }
      const { message, type, extraOptions } = parsed;
      if (type === "success") notify.success(message, extraOptions);
      else if (type === "error") notify.error(message, extraOptions);
      else if (type === "info") notify.info(message, extraOptions);
      else if (type === "warning") notify.warn(message, extraOptions);
      // yield one microtask so the toast registers before cleanup
      await Promise.resolve();
      sessionStorage.removeItem(PERSISTED_TOAST_KEY);
    }
  },
};
